import { useRef, useState, type CSSProperties, type PointerEvent } from 'react';
import { useImageFileDrop } from './useImageFileDrop';
import type { GoodsItemImageConfig } from '@lub0v/santa-utils';

export function goodsImageStyle(config: Partial<GoodsItemImageConfig> = {}): CSSProperties {
  const { x = 50, y = 50, scale = 1 } = config;
  return {
    objectPosition: `${x}% ${y}%`,
    transformOrigin: `${x}% ${y}%`,
    transform: `scale(${scale})`,
  };
}

const clamp = (value: number, min: number, max: number) => Math.min(max, Math.max(min, value));

type Drag = {
  pointer: number;
  left: number;
  top: number;
  x: number;
  y: number;
  overflowX: number;
  overflowY: number;
  scale: number;
};

export function GoodsImageEditor({
  image,
  config,
  onChange,
  onSelectFile,
  onDropFile,
  onRemove,
  disabled,
  ru,
}: {
  image: string;
  config: GoodsItemImageConfig;
  onChange: (value: GoodsItemImageConfig) => void;
  onSelectFile: () => void;
  onDropFile: (file: File) => void;
  onRemove: () => void;
  disabled: boolean;
  ru: boolean;
}) {
  const words = (en: string, russian: string) => (ru ? russian : en);
  const { isFileOver, dropHandlers } = useImageFileDrop(disabled, onDropFile);
  const img = useRef<HTMLImageElement>(null);
  const drag = useRef<Drag | null>(null);
  const moved = useRef(false);
  const [loaded, setLoaded] = useState('');
  const [failed, setFailed] = useState('');
  const [dragging, setDragging] = useState(false);
  const ready = Boolean(image && loaded === image && failed !== image && !disabled);
  function start(event: PointerEvent<HTMLDivElement>) {
    moved.current = false;
    if (!ready || event.button !== 0 || !event.isPrimary || !img.current) return;
    const bounds = event.currentTarget.getBoundingClientRect();
    const cover = Math.max(
      bounds.width / img.current.naturalWidth,
      bounds.height / img.current.naturalHeight,
    );
    // The card uses cover + a focal-point transform. Its pixel offset is
    // -overflow * focalPoint / 100, so dragging follows the pointer exactly.
    drag.current = {
      pointer: event.pointerId,
      left: event.clientX,
      top: event.clientY,
      x: config.x,
      y: config.y,
      scale: config.scale,
      overflowX: Math.max(0, img.current.naturalWidth * cover * config.scale - bounds.width),
      overflowY: Math.max(0, img.current.naturalHeight * cover * config.scale - bounds.height),
    };
    event.currentTarget.setPointerCapture(event.pointerId);
    event.currentTarget.focus({ preventScroll: true });
    setDragging(true);
    event.preventDefault();
  }
  function move(event: PointerEvent<HTMLDivElement>) {
    const current = drag.current;
    if (!ready || !current || current.pointer !== event.pointerId) return;
    if (Math.hypot(event.clientX - current.left, event.clientY - current.top) > 4)
      moved.current = true;
    if (!moved.current) return;
    onChange({
      scale: current.scale,
      x:
        current.overflowX > 0.5
          ? clamp(current.x - ((event.clientX - current.left) / current.overflowX) * 100, 0, 100)
          : current.x,
      y:
        current.overflowY > 0.5
          ? clamp(current.y - ((event.clientY - current.top) / current.overflowY) * 100, 0, 100)
          : current.y,
    });
  }
  function end() {
    drag.current = null;
    setDragging(false);
  }
  function zoom(delta: number) {
    onChange({ ...config, scale: clamp(Math.round((config.scale + delta) * 100) / 100, 1, 3) });
  }
  return (
    <div className="goods-image-editor">
      <div className="goods-image-editor__controls">
        <div
          className={`admin-goods__image goods-image-editor__frame${dragging ? ' is-dragging' : ''}${isFileOver ? ' is-file-over' : ''}`}
          {...dropHandlers}
          role="button"
          tabIndex={disabled ? -1 : 0}
          aria-disabled={disabled}
          aria-label={words(
            'Choose image; drag to position it or use arrow keys',
            'Выбрать изображение; перетащите или используйте стрелки для настройки',
          )}
          onClick={() => {
            if (!disabled && !moved.current) onSelectFile();
            moved.current = false;
          }}
          onPointerDown={start}
          onPointerMove={move}
          onPointerUp={end}
          onPointerCancel={end}
          onLostPointerCapture={end}
          onKeyDown={(event) => {
            if (!disabled && (event.key === 'Enter' || event.key === ' ')) {
              event.preventDefault();
              onSelectFile();
              return;
            }
            if (!ready || !['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'].includes(event.key))
              return;
            event.preventDefault();
            const step = event.shiftKey ? 1 : 5;
            onChange({
              ...config,
              x: clamp(
                config.x +
                  (event.key === 'ArrowLeft' ? step : event.key === 'ArrowRight' ? -step : 0),
                0,
                100,
              ),
              y: clamp(
                config.y + (event.key === 'ArrowUp' ? step : event.key === 'ArrowDown' ? -step : 0),
                0,
                100,
              ),
            });
          }}
        >
          {image && failed !== image ? (
            <img
              ref={img}
              src={image}
              alt=""
              draggable={false}
              style={goodsImageStyle(config)}
              onLoad={() => {
                setLoaded(image);
                setFailed('');
              }}
              onError={() => {
                setFailed(image);
                setLoaded('');
              }}
            />
          ) : (
            <span className="goods-image-editor__empty">
              {failed && failed === image
                ? words('Image unavailable', 'Изображение недоступно')
                : words('Click to add an image', 'Нажмите, чтобы добавить фото')}
            </span>
          )}
          {isFileOver && (
            <span className="goods-image-editor__drop-hint">
              {words('Drop image to upload', 'Отпустите фото для загрузки')}
            </span>
          )}
          {ready && (
            <span className="goods-image-editor__drag-hint" aria-hidden="true">
              ↔ ↕
            </span>
          )}
        </div>
        <div className="goods-image-editor__tools">
          <div className="goods-image-editor__zoom">
            <button
              type="button"
              aria-label={words('Zoom out', 'Уменьшить')}
              disabled={!ready || config.scale <= 1}
              onClick={() => zoom(-0.05)}
            >
              −
            </button>
            <output aria-label={words('Zoom', 'Масштаб')}>{Math.round(config.scale * 100)}%</output>
            <button
              type="button"
              aria-label={words('Zoom in', 'Увеличить')}
              disabled={!ready || config.scale >= 3}
              onClick={() => zoom(0.05)}
            >
              +
            </button>
          </div>
          <div className="goods-image-editor__image-actions">
            <button
              type="button"
              disabled={!ready}
              onClick={() => onChange({ x: 50, y: 50, scale: 1 })}
            >
              {words('Reset', 'Сбросить')}
            </button>
            <button type="button" disabled={disabled || !image} onClick={onRemove}>
              {words('Remove', 'Удалить')}
            </button>
          </div>
          <p>
            {words(
              'Click or drop a file to upload · Drag to position · Up to 2 MB',
              'Нажмите или перетащите файл · Тяните для сдвига · До 2 МБ',
            )}
          </p>
        </div>
      </div>
    </div>
  );
}
